import React, { useContext, useState } from "react";

import {
  Button,
  CircularProgress,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";

import { FeatureFlagSegment } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/feature_flag_pb";

import {
  addFeatureFlagSegmentMembers,
  removeFeatureFlagSegmentMembers,
} from "api/endpoints/feature-flag-segments";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { SnackbarContext } from "context/snackbar-context";
import { getScUserEmail } from "helpers/get-sc-user-email";
import { getScUserName } from "helpers/get-sc-user-name";
import { toUUID } from "helpers/s12-helpers";

interface OrgSegmentRecordProps {
  segmentListInfo: FeatureFlagSegment;
  regionCode: string;
  roleId: string;
  onSuccess: () => void;
}

const OrgSegmentRecord = ({
  segmentListInfo,
  regionCode,
  roleId,
  onSuccess,
}: OrgSegmentRecordProps) => {
  const { showError } = useContext(SnackbarContext);
  const [addSegmentModalOpen, setAddSegmentModalOpen] = useState(false);
  const [removeSegmentModalOpen, setRemoveSegmentModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleAddSubmit = async () => {
    setIsLoading(true);
    const scUserName = await getScUserName();
    const scUserEmail = await getScUserEmail();
    addFeatureFlagSegmentMembers(
      {
        segmentKey: segmentListInfo.key,
        organisationIds: [toUUID(roleId)],
        employeeEmail: scUserEmail,
        employeeName: scUserName,
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then(() => {
        onSuccess();
      })
      .catch(showError)
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleRemoveSubmit = async () => {
    setIsLoading(true);
    const scUserName = await getScUserName();
    const scUserEmail = await getScUserEmail();
    removeFeatureFlagSegmentMembers(
      {
        segmentKey: segmentListInfo.key,
        organisationIds: [toUUID(roleId)],
        employeeEmail: scUserEmail,
        employeeName: scUserName,
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then(() => {
        onSuccess();
      })
      .catch(showError)
      .finally(() => {
        setIsLoading(false);
      });
  };

  const showActionButton = () => {
    if (isLoading) {
      return (
        <CircularProgress
          color="primary"
          variant="indeterminate"
          thickness={4}
          size={30}
          data-testid="loading-icon"
        />
      );
    }

    if (!segmentListInfo.organisationIds.includes(toUUID(roleId))) {
      return (
        <Button
          variant="contained"
          color="primary"
          size="medium"
          disabled={!segmentListInfo.editable}
          onClick={() => {
            setAddSegmentModalOpen(true);
          }}
        >
          Add Org
        </Button>
      );
    }

    return (
      <Button
        variant="contained"
        color="secondary"
        size="medium"
        disabled={!segmentListInfo.editable}
        onClick={() => {
          setRemoveSegmentModalOpen(true);
        }}
      >
        Remove Org
      </Button>
    );
  };

  return (
    <>
      <TableRow>
        <>
          <TableCell style={{ width: "25%", paddingLeft: "30px" }}>
            <Typography variant="body2" display="inline">
              <b> Name: {segmentListInfo.name}</b>
              <br />
              Key: {segmentListInfo.key}
            </Typography>
          </TableCell>
          <TableCell>{segmentListInfo.description}</TableCell>
          <TableCell>
            {segmentListInfo.tags.length !== 0 ? (
              segmentListInfo.tags.map((tagsListInfo, index) => {
                return <div key={index}>{tagsListInfo}</div>;
              })
            ) : (
              <div>-</div>
            )}
          </TableCell>
          <TableCell>{showActionButton()}</TableCell>
        </>
      </TableRow>

      <ConfirmDialog
        title="Add organisation to segment"
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        open={addSegmentModalOpen}
        setOpen={setAddSegmentModalOpen}
        onConfirm={handleAddSubmit}
      >
        {segmentListInfo.name}
      </ConfirmDialog>
      <ConfirmDialog
        title="Remove organisation from segment"
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        open={removeSegmentModalOpen}
        setOpen={setRemoveSegmentModalOpen}
        onConfirm={handleRemoveSubmit}
      >
        {segmentListInfo.name}
      </ConfirmDialog>
    </>
  );
};

export default OrgSegmentRecord;
